<template>
  <dialog
    :class="[props.variant || 'large', { blurred: blur, closeable, compact }]"
    ref="dialog"
    @close="show = false"
    @cancel="onCancel"
  >
    <!-- The dialog itself is a non-scrolling frame (so the close button and
         rounded corners stay put); this inner wrapper is what scrolls when
         the content is taller than the viewport. -->
    <div class="dialog-body">
      <slot />
    </div>
    <Button variant="close" @click="show = false" v-if="closeable" />
  </dialog>
</template>

<script setup lang="ts">
import { ref, watch, onMounted, onBeforeUnmount } from 'vue'
import Button from '@/components/Button.vue'

const props = defineProps<{
  variant?: 'small' | 'medium' | 'large'
  blur?: boolean
  closeable?: boolean
  preventClose?: boolean
  compact?: boolean
}>()

const dialog = ref<HTMLDialogElement>()
const show = defineModel('show', { required: true, type: Boolean, default: false })

// ---- page-scroll lock ----------------------------------------------------
// showModal() promotes the dialog to the top layer and makes the page inert,
// but WebKit/Capacitor still lets the document behind rubber-band scroll.
// Reference-count opens (TrainingView can mount several Dialogs even though
// only one is shown at a time) so the lock releases exactly when the last
// open dialog closes.
let locked = false
function lockScroll(lock: boolean): void {
  if (lock === locked) return
  locked = lock
  openCount += lock ? 1 : -1
  document.documentElement.classList.toggle('dialog-open', openCount > 0)
}

function open(): void {
  if (dialog.value?.open) return
  dialog.value?.showModal()
  lockScroll(true)
}

function close(): void {
  if (dialog.value?.open) dialog.value.close()
  lockScroll(false)
}

function onCancel(event: Event): void {
  if (props.preventClose) event.preventDefault()
}

onMounted(() => {
  if (show.value) open()
})

watch(show, (newShow) => {
  if (newShow) open()
  else close()
})

// A dialog can be torn down (v-if) while still open — release its lock so the
// page doesn't stay frozen.
onBeforeUnmount(() => lockScroll(false))
</script>

<script lang="ts">
// Module-scoped so every Dialog instance shares one count.
let openCount = 0
</script>

<style scoped lang="css">
dialog {
  background: rgba(243, 246, 252, 1);
  border: 0;
  border-radius: 15px;

  /* showModal() promotes the element to the top layer; position:fixed resolves
     against the viewport. inset:0 + margin:auto is the correct centering
     idiom for <dialog> — browsers implement this at the top-layer level and
     it works in Firefox/Chrome/Safari modal dialogs. */
  position: fixed;
  inset: 0;
  z-index: 2;
  margin: auto;

  /* When the on-screen keyboard is up, shift the dialog up by half the keyboard
     height so it re-centers in the *visible* area above the keyboard. Without
     this, a focused input (e.g. the pairing OTP) drops behind the keyboard and
     iOS WKWebView shoves the whole dialog off the top. A transform is used
     rather than a bottom-inset override: overriding `bottom` while `top: 0` is
     still set turns the box into a top+bottom constraint that (with max-height)
     stops centering and pins the dialog to the top. transform doesn't touch
     layout, so the inset:0 + margin:auto centering above is preserved.
     --keyboard-inset-bottom is published on :root by installKeyboardInset()
     (lib/keyboard-inset.ts) and is 0 with no keyboard → a no-op elsewhere
     (incl. tablet). */
  transform: translateY(calc(var(--keyboard-inset-bottom, 0px) * -0.5));

  /* Never exceed the visible viewport (minus a gutter, safe-area insets, and
     the keyboard). When the content is taller, the inner .dialog-body scrolls —
     the page behind it does not. */
  max-height: calc(
    100dvh - 2rem - var(--safe-area-inset-top) - var(--safe-area-inset-bottom) -
      var(--keyboard-inset-bottom, 0px)
  );

  /* Clip the scrolling body to the rounded corners and keep the absolutely
     positioned close button inside the frame. Padding lives on .dialog-body
     so the scrollbar sits inside the rounded edge. The flex layout is gated
     on [open] below so it doesn't override the UA's
     `dialog:not([open]) { display: none }` and leave closed dialogs visible. */
  overflow: hidden;
  padding: 0;

  &[open] {
    display: flex;
    flex-direction: column;
  }

  button.close {
    padding: 0.5rem;

    position: absolute;
    top: 0.5rem;
    right: 0.5rem;
    z-index: 1;
  }

  div.dialog-body {
    overflow-y: auto;
    /* Keep an overscroll bounce inside the dialog instead of chaining to the
       (locked) page behind it. */
    overscroll-behavior: contain;
    -webkit-overflow-scrolling: touch;

    padding: 2.25rem 2rem 2rem 2rem;

    /* Tighten the body gutters on a narrow phone so the fixed desktop
       padding doesn't eat a third of a 360px-wide screen. The closeable
       top-padding override below is more specific, so the X-button
       clearance is preserved. */
    @media (max-width: 600px) {
      padding: 1.5rem 1.25rem;
    }
  }

  &.compact div.dialog-body {
    padding: 1.25rem 1.5rem 1.25rem;
  }

  /* When the X is present, drop the body's top padding so the first line of
     content clears the close button. */
  &.closeable div.dialog-body {
    padding-top: 4rem;
  }

  &.small {
    width: 95%;
    max-width: 375px;
  }

  &.medium {
    width: 95%;
    max-width: 700px;
  }

  &.large {
    width: 95%;
    max-width: 1024px;
  }

  :slotted(h1) {
    font-size: 1.25rem !important;
    font-weight: 700;
    line-height: 135%;
    text-align: left;

    margin-bottom: 0.5ch;
  }

  :slotted(p) {
    font-size: 1.2rem;
    line-height: 145%;

    margin-bottom: 1.5ch;

    &:slotted(.note) {
      font-size: 0.9rem !important;
      line-height: 145%;

      opacity: 0.65;
    }
  }
}

/* Native backdrop — covers the whole viewport, sits directly behind the
   top-layer dialog, and blocks interaction with the page. Replaces the old
   normal-flow .dialog-blur div, which only covered one viewport-height from
   the document top and let you scroll/tap behind it. A plain dim is the
   default; `blur` swaps in the brand-tinted blur. */
dialog::backdrop {
  background: rgba(0, 0, 0, 0.3);
}

dialog.blurred::backdrop {
  background: rgba(0, 142, 207, 0.3);
  backdrop-filter: blur(8px);
}

/* A halo on the close glyph, in the dialog's own background colour. Against the
   plain dialog surface it matches what is behind it and so is invisible; it only
   resolves where something else has been drawn underneath — which in practice
   means GarlandBanner, whose bunting spans the top edge and runs right under
   this corner, leaving the X to read as part of the pattern. Doubled because one
   2px shadow is too thin to separate a 2px stroke.

   Deliberately NOT nested inside the `button.close` rule above: this file uses
   native CSS nesting, where Vue places the scope attribute after the nested
   selector — `button.close [data-v-x] img` — which asks for a scoped element
   *inside* the button and matches nothing. Flat, `:deep()` attaches the
   attribute to `button.close` itself, which is where it belongs (the <img> is
   Button's, and carries no scope id of ours). */
button.close :deep(img) {
  filter: drop-shadow(0 0 2px rgba(243, 246, 252, 1))
    drop-shadow(0 0 2px rgba(243, 246, 252, 1));
}
</style>